/**
 * `mountTimeline(container, ctx)` — the frame-based timeline / dope sheet (SPEC "Timeline /
 * Dope Sheet"), the core editing surface of ANIMATE mode.
 *
 * Composition: a DOM transport row (`transport.ts`) above a canvas dope sheet (`dopesheet.ts`),
 * with window-level shortcuts (`shortcuts.ts`). All geometry is pure (`layout.ts`) and every
 * mutation goes through store actions.
 *
 * Redraws are scheduled on an animation frame: store listeners fire synchronously inside an
 * action, and dispatching from there would re-enter the store.
 */

import './timeline.css';

import type { EditorContext } from '../context';
import type { EditorState } from '../index';
import { mountDopesheet } from './dopesheet';
import { DISABLED_MESSAGE } from './render';
import { installShortcuts } from './shortcuts';
import { mountTransport } from './transport';

export interface TimelineHandle {
  dispose(): void;
}

/** Slices the timeline draws from — a change in any of them schedules a repaint. */
function watched(state: EditorState): unknown[] {
  return [
    state.mode,
    state.clip,
    state.playhead,
    state.keySelection,
    state.timeline,
    state.selection,
    state.livePose,
    state.history,
  ];
}

/**
 * Chrome never fires `requestAnimationFrame` in a hidden or background tab, and the timeline's
 * whole DOM sync (placeholder ⇄ transport ⇄ sheet, not just the canvas paint) rides on this
 * scheduler. Without a fallback the timeline silently freezes at whatever it last drew — which is
 * exactly what a headless run driving `window.__intchMotionKit` sees. So: rAF when frames are flowing,
 * a timeout as the floor, and whichever arrives first cancels the other.
 */
const BACKGROUND_FRAME_MS = 100;

function scheduleFrame(run: () => void): () => void {
  let done = false;
  const fire = (): void => {
    if (done) return;
    done = true;
    run();
  };

  const timer = setTimeout(fire, BACKGROUND_FRAME_MS);
  if (typeof requestAnimationFrame !== 'function') {
    return () => {
      done = true;
      clearTimeout(timer);
    };
  }

  const raf = requestAnimationFrame(() => fire());
  return () => {
    done = true;
    clearTimeout(timer);
    cancelAnimationFrame(raf);
  };
}

export function mountTimeline(container: HTMLElement, ctx: EditorContext): TimelineHandle {
  const store = ctx.store;

  const root = document.createElement('div');
  root.className = 'tl-root';
  container.appendChild(root);

  const placeholder = document.createElement('div');
  placeholder.className = 'tl-placeholder';
  placeholder.dataset.tl = 'placeholder';
  placeholder.textContent = DISABLED_MESSAGE;
  root.appendChild(placeholder);

  const transport = mountTransport(root, ctx);
  const sheet = mountDopesheet(root, ctx);
  const removeShortcuts = installShortcuts(ctx, sheet.element);

  let cancel: (() => void) | null = null;
  let disposed = false;

  function render(): void {
    if (disposed) return;
    const active = store.getState().mode === 'animate';
    root.classList.toggle('tl-inactive', !active);
    placeholder.hidden = active;
    transport.element.hidden = !active;
    sheet.element.hidden = !active;
    transport.sync();
    if (active) sheet.redraw();
  }

  function schedule(): void {
    if (disposed || cancel) return;
    cancel = scheduleFrame(() => {
      cancel = null;
      render();
    });
  }

  let previous = watched(store.getState());
  const unsubscribe = store.subscribe((state) => {
    const next = watched(state);
    if (next.every((slice, index) => slice === previous[index])) return;
    previous = next;
    schedule();
  });

  render();

  return {
    dispose(): void {
      disposed = true;
      cancel?.();
      cancel = null;
      unsubscribe();
      removeShortcuts();
      transport.dispose();
      sheet.dispose();
      root.remove();
    },
  };
}

export { DISABLED_MESSAGE } from './render';
export type { DopesheetHandle } from './dopesheet';
export type { TransportHandle } from './transport';
